// Aggiunta del sito alla schermata Home.
//
// Android/Chrome: il browser dà un evento (beforeinstallprompt) che si può
// tenere da parte e lanciare da un nostro tasto. iPhone/iPad: non esiste
// nessuna API per farlo da una pagina, l'unica strada è il menu Condividi di
// Safari: qui si decide solo se mostrare la guida ai passaggi.
//
// L'evento arriva una volta sola e presto: initInstallPrompt() va chiamata
// all'avvio dell'app (main.js), prima che qualunque componente sia montato.
import { ref, computed } from 'vue'

const deferred = ref(null)
const installed = ref(false)
let initialized = false

export function initInstallPrompt() {
  if (initialized || typeof window === 'undefined') return
  initialized = true
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferred.value = e
  })
  window.addEventListener('appinstalled', () => {
    installed.value = true
    deferred.value = null
  })
}

// Già aperto dalla schermata Home (senza barre del browser).
export function isStandalone() {
  if (typeof window === 'undefined') return false
  if (window.navigator && window.navigator.standalone === true) return true
  return typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches
}

// iPhone/iPad (iPadOS si presenta come Mac, lo distingue il multitouch) fuori
// dai browser dentro le app (Instagram, Facebook…), che non hanno "Aggiungi a
// Home".
export function isIosBrowser() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  return ios && !/FBAN|FBAV|Instagram|Line\//.test(ua)
}

export function useInstallPrompt() {
  initInstallPrompt()
  const standalone = isStandalone()
  // Tasto vero: il browser ha dato l'evento. Guida: iOS, dove non c'è altro.
  const canPrompt = computed(() => !standalone && !installed.value && !!deferred.value)
  const needsGuide = computed(() => !standalone && !installed.value && !deferred.value && isIosBrowser())
  const available = computed(() => canPrompt.value || needsGuide.value)

  // 'accepted' | 'dismissed' | 'guide' (niente evento: mostrare i passaggi).
  async function install() {
    const event = deferred.value
    if (!event) return 'guide'
    deferred.value = null
    try {
      await event.prompt()
      const choice = await event.userChoice
      return choice && choice.outcome === 'accepted' ? 'accepted' : 'dismissed'
    } catch (e) {
      return 'dismissed'
    }
  }

  return { available, canPrompt, needsGuide, install }
}

// Solo per i test.
export function __resetInstallPromptForTests() {
  deferred.value = null
  installed.value = false
  initialized = false
}
